import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  Box,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { DocumentDTO, AttachmentDTO } from "../../types/DocumentDTO";
import { useState } from "react";
import DocumentPreviewModal from "../common/DocumentPreviewModal";
import AddAttachmentModal from "./AddAttachmentModal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { deleteDocumentAttachment, getDocumentAttachmentFile } from "../../api/documents";
import ConfirmDeleteAttachmentDialog from "./ConfirmDeleteAttachmentDialog";
import AddIcon from "@mui/icons-material/Add";
import { useAppTranslation } from "../../hooks/useAppTranslation";

// const API_URL = process.env.REACT_APP_API_URL;

function getAttachmentMeta(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase();

  if (ext === "pdf") {
    return { icon: <PictureAsPdfIcon color="error" />, type: "PDF" };
  }

  if (["jpg", "jpeg", "png", "webp"].includes(ext ?? "")) {
    return { icon: <ImageIcon color="primary" />, type: "OBRAZ" };
  }

  return { icon: <AttachFileIcon />, type: ext?.toUpperCase() ?? "PLIK" };
}

type Props = {
  attachments: DocumentDTO["attachments"];
  companyId: string;
  documentId: string;
  documentStatus: string | null;
  onReload: () => void;
};

export default function DocumentAttachments({
  attachments,
  companyId,
  documentId,
  documentStatus,
  onReload,
}: Props) {
  const { t } = useAppTranslation("common");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<AttachmentDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AttachmentDTO | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const normalizedStatus = documentStatus?.toLowerCase();

  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isLocked =
    normalizedStatus === "approved" ||
    normalizedStatus === "posted";

  const requestDelete = (attachment: AttachmentDTO) => {
    setDeleteTarget(attachment);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await deleteDocumentAttachment(
        companyId,
        documentId,
        deleteTarget.id
      );
      setDeleteTarget(null);
      onReload();
    } catch (e: any) {
      setDeleteError(e?.message || t("Błąd usuwania załącznika"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePreview = async (attachment: AttachmentDTO) => {
    setSelected(attachment);
    setPreviewLoading(true);
    setPreviewError(null);
    setFileBase64(null);

    try {
      const base64 = await getDocumentAttachmentFile(
        companyId,
        documentId,
        attachment.id
      );
      setFileBase64(base64);
    } catch (e: any) {
      setPreviewError(e.message || t("Błąd pobierania pliku"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = () => {
    if (!selected || !fileBase64) return;
    
    const link = document.createElement("a");
    link.href = `data:application/octet-stream;base64,${fileBase64}`;
    link.download = selected.filename;
    link.click();
  };

  const handleClosePreview = () => {
    setSelected(null);
    setFileBase64(null);
    setPreviewError(null);
  };

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1">{t("Załączniki")}</Typography>
          <Tooltip
            title={
              isLocked
                ? t("Nie można dodawać załączników dla dokumentów zatwierdzonych lub zaksięgowanych")
                : ""
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={isLocked}
                onClick={() => setAddOpen(true)}
              >
                {t("Dodaj")}
              </Button>
            </span>
          </Tooltip>
        </Box>
        {deleteError && (
          <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 2 }}>
            {deleteError}
          </Alert>
        )}

        {/* CONTENT */}
        {attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("Brak załączników")}
          </Typography>
        ) : (
          <List>
            {attachments.map((a) => {
              const meta = getAttachmentMeta(a.filename);
              return (
                <ListItem
                  key={a.id}
                  disablePadding
                  secondaryAction={
                    <Tooltip
                      title={
                        isLocked
                          ? t("Nie można usuwać załączników dla dokumentów zatwierdzonych lub zaksięgowanych")
                          : t("Usuń załącznik")
                      }
                    >
                      <span>
                        <IconButton
                          edge="end"
                          disabled={isLocked}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDelete(a);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemButton onClick={() => handlePreview(a)}>
                    <ListItemIcon>{meta.icon}</ListItemIcon>
                    <ListItemText
                      primary={a.filename}
                      secondary={meta.type}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      {/* PREVIEW */}
      <DocumentPreviewModal
        open={!!selected}
        title={selected?.filename ?? t("Podgląd załącznika")}
        loading={previewLoading}
        error={previewError}
        fileBase64={fileBase64}
        fileName={selected?.filename ?? null}
        onClose={handleClosePreview}
        onDownload={handleDownload}
      />

      {/* ADD MODAL */}
      <AddAttachmentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        companyId={companyId}
        documentId={documentId}
        existingFilenames={attachments.map((a) => a.filename)}
        onSuccess={onReload}
      />
      <ConfirmDeleteAttachmentDialog
        open={!!deleteTarget}
        filename={deleteTarget?.filename ?? null}
        loading={deleteLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
